import React, { useEffect } from 'react';
import {
  AuthenticatedTemplate,
  MsalProvider,
  MsalAuthenticationTemplate,
} from '@azure/msal-react';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { msalConfigCreator, loginRequest } from './services/users/authConfig';
import App from './App';
import { fetchSettings } from './store/settings/actions';

import { RootState } from './store/store';
import AuthInstanceWrapper from './AuthInstanceWrapper';
import NotAuthorized from './components/layout/NotAuthorized';
import SignedOut from './components/layout/SignedOut';

const SIGNED_OUT_PATH = '/signed-out';

/**
 * This component creates the msal instance, of which there may only be one
 * in existence.
 */
const AuthWrapper = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const settings = useSelector((state: RootState) => state.settings.settings);
  const signingOut = window.location.pathname === SIGNED_OUT_PATH;

  useEffect(() => {
    if (!settings) {
      dispatch(fetchSettings());
    }
  }, [dispatch, settings]);

  // Signing out of EWorkbook clears only its own MSAL cache. The Entra session
  // stays, so the configurator and Turvakortti stay signed in. This runs here,
  // outside MsalAuthenticationTemplate, which would sign the user straight back in.
  useEffect(() => {
    if (!signingOut || !settings) {
      return;
    }

    new PublicClientApplication(msalConfigCreator(settings))
      .logoutRedirect({ onRedirectNavigate: () => false })
      .catch((err) => console.error('Error signing out', err));
  }, [signingOut, settings]);

  if (signingOut) {
    return <SignedOut />;
  }

  if (!settings) {
    return null;
  }

  const msalConfig = msalConfigCreator(settings);
  const msalInstance = new PublicClientApplication(msalConfig);

  return (
    <Router>
      <Switch>
        <Route exact path="/ad-login">
          <NotAuthorized />
        </Route>
      </Switch>
      <Route>
        <MsalProvider instance={msalInstance}>
          <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={loginRequest()}
          >
            <AuthInstanceWrapper>
              <AuthenticatedTemplate>
                <App />
              </AuthenticatedTemplate>
            </AuthInstanceWrapper>
          </MsalAuthenticationTemplate>
        </MsalProvider>
      </Route>
    </Router>
  );
};

export default AuthWrapper;
